// /var/www/oszczedzam/client/src/pages/Budgets.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Button, Dialog, DialogContent, 
  DialogTitle, DialogActions, DialogContentText, CircularProgress, 
  Alert, Snackbar, Tab, Tabs, Paper
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import BudgetForm from '../components/budgets/BudgetForm';
import BudgetCard from '../components/budgets/BudgetCard';
import { useBudgets } from '../hooks/useBudgets';
import { useTransactions } from '../hooks/useTransactions';
import { useAuth } from '../contexts/AuthContext';

function Budgets() {
  const { budgets, loading: loadingBudgets, error: budgetsError, addBudget, updateBudget, deleteBudget } = useBudgets();
  const { transactions, loading: loadingTransactions } = useTransactions();
  const [openForm, setOpenForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [budgetToDelete, setBudgetToDelete] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [tabValue, setTabValue] = useState(0); // 0: all, 1: low usage, 2: high usage
  
  // Oblicz wydatki dla każdej kategorii
  const calculateCategorySpending = () => {
    const spending = {};
    
    // Inicjalizuj wszystkie kategorie budżetów z zerowym wydatkiem
    budgets.forEach(budget => {
      spending[budget.category] = 0;
    });
    
    // Zsumuj wydatki dla każdej kategorii
    transactions.forEach(transaction => {
      // Tylko wydatki (kwoty ujemne)
      if (transaction.amount < 0 && spending[transaction.category] !== undefined) {
        const currentDate = new Date();
        const transactionDate = new Date(transaction.date);
        
        // Sprawdź czy transakcja znajduje się w aktualnym okresie
        if (isTransactionInCurrentPeriod(transactionDate, currentDate, getBudgetForCategory(transaction.category)?.period)) {
          spending[transaction.category] += Math.abs(transaction.amount);
        }
      }
    });
    
    return spending;
  };
  
  // Sprawdź czy transakcja jest w bieżącym okresie budżetowym
  const isTransactionInCurrentPeriod = (transactionDate, currentDate, period) => {
    if (!period) return false;
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    
    if (period === 'monthly') {
      // Ten sam miesiąc i rok
      return transactionDate.getMonth() === month && transactionDate.getFullYear() === year;
    } else if (period === 'weekly') {
      // Oblicz datę początku bieżącego tygodnia (poniedziałek)
      const weekStart = new Date(year, month, day - currentDate.getDay() + 1);
      return transactionDate >= weekStart;
    } else if (period === 'yearly') {
      // Ten sam rok
      return transactionDate.getFullYear() === year;
    }
    
    return false;
  };
  
  // Znajdź budżet dla danej kategorii
  const getBudgetForCategory = (category) => {
    return budgets.find(budget => budget.category === category);
  };
  
  // Filtruj budżety na podstawie wybranej zakładki
  const filterBudgetsByTab = () => {
    const categorySpending = calculateCategorySpending();
    
    return budgets.filter(budget => {
      const percentUsed = (categorySpending[budget.category] / budget.limit) * 100;
      
      if (tabValue === 0) { // Wszystkie
        return true;
      } else if (tabValue === 1) { // Niskie wykorzystanie (< 70%)
        return percentUsed < 70;
      } else if (tabValue === 2) { // Wysokie wykorzystanie (>= 70%)
        return percentUsed >= 70;
      }
      
      return true;
    });
  };
  
  // Obsługa zmiany zakładki
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Dodaj nowy budżet
  const handleAddBudget = async (budgetData) => {
    try {
      await addBudget(budgetData);
      setOpenForm(false);
      showNotification('Budżet został dodany pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas dodawania budżetu:', error);
      showNotification('Nie udało się dodać budżetu. Spróbuj ponownie później.', 'error');
    }
  };

  // Aktualizuj budżet
  const handleUpdateBudget = async (budgetData) => {
    try {
      await updateBudget(editingBudget.id, budgetData);
      setOpenForm(false);
      setEditingBudget(null);
      showNotification('Budżet został zaktualizowany pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas aktualizacji budżetu:', error);
      showNotification('Nie udało się zaktualizować budżetu. Spróbuj ponownie później.', 'error');
    }
  };

  // Usuń budżet
  const handleDeleteBudget = async () => {
    try {
      await deleteBudget(budgetToDelete);
      setDeleteDialogOpen(false);
      setBudgetToDelete(null);
      showNotification('Budżet został usunięty pomyślnie!', 'success');
    } catch (error) {
      console.error('Błąd podczas usuwania budżetu:', error);
      showNotification('Nie udało się usunąć budżetu. Spróbuj ponownie później.', 'error');
    }
  };

  // Otwórz formularz edycji budżetu
  const handleEditBudget = (budget) => {
    setEditingBudget(budget);
    setOpenForm(true);
  };

  // Otwórz dialog potwierdzenia usunięcia
  const handleOpenDeleteDialog = (budgetId) => {
    setBudgetToDelete(budgetId);
    setDeleteDialogOpen(true);
  };

  // Pokaż powiadomienie
  const showNotification = (message, type) => {
    setNotification({
      open: true,
      message,
      type
    });
  };

  // Zamknij powiadomienie
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Pobierz wydatki dla każdej kategorii
  const categorySpending = calculateCategorySpending();
  
  // Filtruj budżety według zakładki
  const filteredBudgets = filterBudgetsByTab();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Budżety</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingBudget(null);
            setOpenForm(true);
          }}
        >
          Nowy budżet
        </Button>
      </Box>
      
      {/* Zakładki */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Wszystkie" />
          <Tab label="Niskie wykorzystanie" />
          <Tab label="Wysokie wykorzystanie" />
        </Tabs>
      </Box>
      
      {/* Komunikat o błędzie */}
      {budgetsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {budgetsError}
        </Alert>
      )}
      
      {/* Lista budżetów */}
      {(loadingBudgets || loadingTransactions) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredBudgets.length > 0 ? (
        <Grid container spacing={3}>
          {filteredBudgets.map((budget) => (
            <Grid item xs={12} sm={6} md={4} key={budget.id}>
              <BudgetCard 
                budget={budget}
                spent={categorySpending[budget.category]}
                onEdit={handleEditBudget}
                onDelete={handleOpenDeleteDialog}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            Nie masz jeszcze żadnych budżetów. Kliknij "Nowy budżet", aby dodać pierwszy.
          </Typography>
        </Paper>
      )}
      
      {/* Formularz budżetu */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <BudgetForm 
            onSubmit={editingBudget ? handleUpdateBudget : handleAddBudget}
            initialData={editingBudget}
            isEditing={!!editingBudget}
          />
        </DialogContent>
      </Dialog>
      
      {/* Dialog potwierdzenia usunięcia */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Czy na pewno chcesz usunąć ten budżet? Tej operacji nie można cofnąć.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anuluj</Button>
          <Button onClick={handleDeleteBudget} color="error" autoFocus>
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Powiadomienia */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Budgets;